<template>
  <div class="invoice-document-view">
    <InvoiceDocumentControls @document-updated="refreshInvoicePreview"/>

    <DocumentHistoryPanel :document-uuid="documentUuid" :refresh-key="historyKey"/>

    <div class="invoice-document-view__preview">
      <iframe
        v-if="invoicePreviewUrl"
        :key="invoicePreviewKey"
        ref="iframeRef"
        :src="invoicePreviewUrl"
        class="invoice-document-view__frame"
        scrolling="no"
        title="Invoice preview"
        @load="onIframeLoad"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onBeforeUnmount, ref, watch} from 'vue';
import {useRoute} from 'vue-router';
import {useOrders} from '@/composables/useOrders';
import InvoiceDocumentControls from '@/components/dashboard/documents/InvoiceDocumentControls.vue';
import DocumentHistoryPanel from '@/components/dashboard/documents/DocumentHistoryPanel.vue';

const MIN_IFRAME_HEIGHT = 720;

const route = useRoute();
const {orderDocument} = useOrders();

const iframeRef = ref<HTMLIFrameElement | null>(null);
const invoicePreviewKey = ref(0);
let iframeResizeObserver: ResizeObserver | null = null;

const documentUuid = computed(() => route.params.uuid as string);

// The preview route is signed. The signature arrives with the document from
// the API; some update responses return the bare model without it, so keep
// the last good one rather than falling back to an unsigned URL.
const lastSignedPreviewUrl = ref<string | null>(null);
watch(() => orderDocument.value?.preview_url, (url) => {
  if (url) {
    lastSignedPreviewUrl.value = url;
  }
}, {immediate: true});

const invoicePreviewUrl = computed(() => lastSignedPreviewUrl.value ?? '');

function syncIframeHeight(): void {
  const iframe = iframeRef.value;
  const doc = iframe?.contentDocument;

  if (!iframe || !doc?.body) {
    return;
  }

  doc.documentElement.style.overflow = 'hidden';
  doc.body.style.overflow = 'hidden';

  const height = Math.max(
    doc.documentElement.scrollHeight,
    doc.body.scrollHeight,
    MIN_IFRAME_HEIGHT,
  );

  iframe.style.height = `${height}px`;
}

function onIframeLoad(): void {
  const doc = iframeRef.value?.contentDocument;

  iframeResizeObserver?.disconnect();
  syncIframeHeight();

  if (!doc?.body) {
    return;
  }

  iframeResizeObserver = new ResizeObserver(syncIframeHeight);
  iframeResizeObserver.observe(doc.documentElement);
  iframeResizeObserver.observe(doc.body);
  void doc.fonts?.ready.then(syncIframeHeight);
}

function refreshInvoicePreview(): void {
  invoicePreviewKey.value += 1;
}

// The history panel reloads whenever the document changes (sends, finalize,
// conversion all touch updated_at).
const historyKey = computed(() => `${orderDocument.value?.updated_at ?? ''}:${orderDocument.value?.sent_at ?? ''}`);

watch(orderDocument, (document, previous) => {
  if (document && previous && document.updated_at !== previous.updated_at) {
    refreshInvoicePreview();
  }
}, {deep: true});

onBeforeUnmount(() => {
  iframeResizeObserver?.disconnect();
});
</script>

<style scoped>
.invoice-document-view {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-width: 0;
  padding-bottom: 32px;
}

.invoice-document-view__preview {
  width: 100%;
  min-width: 0;
  padding: 24px 16px 32px;
  border-radius: 12px;
  background: #bdbdbd;
  border: 1px solid rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.invoice-document-view__frame {
  display: block;
  width: 100%;
  min-height: 720px;
  height: 720px;
  border: 0;
  border-radius: 2px;
  background: rgb(204, 204, 204);
}
</style>

<style>
.v-theme--dark .invoice-document-view__preview {
  background: #757575;
  border-color: rgba(255, 255, 255, 0.16);
}

.v-theme--dark .invoice-document-view__frame {
  background: rgb(204, 204, 204);
}
</style>
